import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import './subscriber-signup.css';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { fetchAllCategoriesWithSub } from '../../../../shared/actions/categoryAction';
import { getUserLocation } from '../../../../shared/utils/locationUtils';
import Interests from './interests';
import SubscriberRegister from './signupForm';
import { isUserLoggedIn } from '../../../../shared/utils/function';
import { subscriberRegistrationSchema } from '../../../../shared/validations/subscriberRegistration';

const SHOW_SUBSCRIBER_INTERESTS = false;

/**
 * SubscriberSignup component manages the two-step subscriber registration process
 * Step 1: Interests - Select interests/categories
 * Step 2: SubscriberRegister - Fill out registration form
 */
const SubscriberSignup = () => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();

  // Step management
  const [currentStep, setCurrentStep] = useState(SHOW_SUBSCRIBER_INTERESTS ? 1 : 2);

  // Categories state
  const [categories, setCategories] = useState({});
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Redirect to home if user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate.toHome();
    }
  }, [isLoggedIn, navigate]);
  
  // Form declaration using subscriberRegistrationSchema
  const form = useForm({
    resolver: zodResolver(subscriberRegistrationSchema),
    defaultValues: {
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      phoneCountry: '+1',
      phoneCountryCode: 'US',
      phoneNumber: '',
      city: '',
      state: '',
      country: '',
      termsAccepted: false,
      selectedInterests: [],
    },
    mode: 'onChange',
  });

  useEffect(() => {
    const fetchUserLocation = async () => {
      const location = await getUserLocation();
      // Set location in form if available and valid
      if (location?.success && location?.city) {
        form.setValue('country', location.country || '');
        form.setValue('state', location.state || '');
        form.setValue('city', location.city || '');
        // Trigger validation to ensure form recognizes the changes
        form.trigger(['country', 'state', 'city']);
      }
    };
    fetchUserLocation();
  }, [form]);

  // Fetch categories on component mount
  useEffect(() => {
    if (!SHOW_SUBSCRIBER_INTERESTS) {
      setCategoriesLoading(false);
      return;
    }

    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await fetchAllCategoriesWithSub();
        if (response.success) {
          setCategories(response.data);
        } else {
          toast.error(response.error || 'Failed to load categories. Please try again.');
        }
      } catch (error) {
        toast.error('Failed to load categories. Please try again.');
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Navigation functions
  const goToNextStep = useCallback(() => {
    if (currentStep < 2) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const goToSignup = useCallback(() => {
    navigate.toSignup();
  }, [navigate]);

  const goToPreviousStep = useCallback(() => {
    if (!SHOW_SUBSCRIBER_INTERESTS) {
      goToSignup();
      return;
    }

    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep, goToSignup]);

  // Handle selected interests from Interests step
  const handleInterestsSelected = useCallback(() => {
    goToNextStep();
  }, [goToNextStep]);

  // Handle registration completion
  const handleRegistrationComplete = useCallback(() => {
    navigate.toSignup();
  }, [navigate]);

  // Render the appropriate step component
  const renderStep = () => {
    if (!SHOW_SUBSCRIBER_INTERESTS) {
      return (
        <SubscriberRegister
          form={form}
          onContinue={handleRegistrationComplete}
          onBack={goToSignup}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <Interests
            form={form}
            onContinue={handleInterestsSelected}
            onBack={goToSignup}
            categories={categories}
            loading={categoriesLoading}
          />
        );
      case 2:
        return (
          <SubscriberRegister
            form={form}
            onContinue={handleRegistrationComplete}
            onBack={goToPreviousStep}
          />
        );
      default:
        return (
          <Interests
            form={form}
            onContinue={handleInterestsSelected}
            onBack={goToSignup}
            categories={categories}
            loading={categoriesLoading}
          />
        );
    }
  };

  return (
    <div className={`subscriber-signup-container`}>
      {renderStep()}
    </div>
  );
};

export default SubscriberSignup;
